import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import * as XLSX from 'xlsx';

const { FiPlus, FiTrash2, FiSave, FiBook, FiCalendar, FiClock, FiAlertCircle, FiRefreshCw, FiUpload, FiDownload, FiInfo, FiX, FiDelete, FiTrash, FiUsers, FiHome } = FiIcons;

const MarksEntry = () => {
  const { state, dispatch, studentService, assessmentService } = useApp();
  const [students, setStudents] = useState([]);
  const [currentAssessments, setCurrentAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [assessmentSyncNeeded, setAssessmentSyncNeeded] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  // Add new state for class group and faculty
  const [classGroup, setClassGroup] = useState('');
  const [classGroupFaculty, setClassGroupFaculty] = useState('');
  const [studentGroups, setStudentGroups] = useState({});
  const [activeGroup, setActiveGroup] = useState(null);
  const fileInputRef = useRef(null);

  // Always use currentAssessments for the table, fallback to mock data only when no course is selected
  const assessments = !state.activeCourse
    ? [
        { id: 'mock_1', type: 'Continuous Assessment', name: 'Quiz 1', marks: 10 },
        { id: 'mock_2', type: 'Continuous Assessment', name: 'Assignment 1', marks: 15 },
        { id: 'mock_3', type: 'Final Assessment', name: 'Final Exam', marks: 60 }
      ]
    : currentAssessments;

  useEffect(() => {
    if (state.activeCourse) {
      loadCourseData();
    } else {
      // Set default data when no course is selected
      setStudents([
        { id: 'temp_1', matricId: 'A20EC001', name: 'Ahmad Ali', marks: {} },
        { id: 'temp_2', matricId: 'A20EC002', name: 'Siti Nurhaliza', marks: {} },
        { id: 'temp_3', matricId: 'A20EC003', name: 'Muhammad Hafiz', marks: {} }
      ]);
      setCurrentAssessments([]);
      setStudentGroups({});
      setActiveGroup(null);
    }
  }, [state.activeCourse]);

  // Check for assessment plan changes
  useEffect(() => {
    if (state.activeCourse && state.assessmentPlan.length > 0) {
      checkForAssessmentPlanChanges();
    }
  }, [state.assessmentPlan, currentAssessments]);

  const loadCourseData = async () => {
    if (!state.activeCourse) return;

    setLoading(true);
    setSaveError(null);
    try {
      console.log('Loading course data for:', state.activeCourse.id);

      // Load assessments and students in parallel
      const [assessmentsResult, studentsResult] = await Promise.all([
        assessmentService.getAssessments(state.activeCourse.id),
        studentService.getStudents(state.activeCourse.id)
      ]);

      // Process assessments
      let formattedAssessments = [];
      if (assessmentsResult.data && assessmentsResult.data.length > 0) {
        formattedAssessments = assessmentsResult.data.map(a => ({
          id: a.id,
          type: a.assessment_type,
          name: a.assessment_name,
          marks: a.marks,
          sltCI: parseFloat(a.slt_ci),
          clo: a.clo_mapping
        }));
        console.log('Loaded assessments:', formattedAssessments);
        setCurrentAssessments(formattedAssessments);
        // Update global state
        dispatch({ type: 'SET_ASSESSMENT_PLAN', payload: formattedAssessments });
      } else {
        console.log('No assessments found for course');
        setCurrentAssessments([]);
        dispatch({ type: 'SET_ASSESSMENT_PLAN', payload: [] });
      }

      // Process students
      let formattedStudents = [];
      const groupedStudents = {};
      
      if (studentsResult.data && studentsResult.data.length > 0) {
        formattedStudents = studentsResult.data.map(student => {
          const marks = {};
          // Convert student marks from array to object format
          if (student.student_marks_clomapper && student.student_marks_clomapper.length > 0) {
            student.student_marks_clomapper.forEach(mark => {
              marks[mark.assessment_id] = mark.marks;
            });
          }
          
          // Get student group data
          const group = student.class_group || 'Ungrouped';
          const faculty = student.faculty || '';
          
          // Initialize group if it doesn't exist
          if (!groupedStudents[group]) {
            groupedStudents[group] = {
              name: group,
              faculty: faculty,
              students: []
            };
          }
          
          // Add student to their group
          const studentObj = {
            id: student.id,
            matricId: student.matric_id,
            name: student.name,
            marks: marks,
            classGroup: group,
            faculty: faculty
          };
          
          groupedStudents[group].students.push(studentObj);
          
          return studentObj;
        });
        
        console.log('Loaded students:', formattedStudents.length);
        console.log('Student groups:', groupedStudents);
        
        // Set the active group to the first one if we have groups
        const groupNames = Object.keys(groupedStudents);
        if (groupNames.length > 0 && !activeGroup) {
          setActiveGroup(groupNames[0]);
          setStudents(groupedStudents[groupNames[0]].students);
        } else if (activeGroup && groupedStudents[activeGroup]) {
          // Keep the current active group if it exists
          setStudents(groupedStudents[activeGroup].students);
        } else {
          // Fallback to all students if no groups or active group doesn't exist
          setStudents(formattedStudents);
          setActiveGroup(null);
        }
        
        setStudentGroups(groupedStudents);
        
        // Update global state with all students
        dispatch({ type: 'SET_STUDENTS_DATA', payload: formattedStudents });
      } else {
        console.log('No students found for course');
        setStudents([]);
        setStudentGroups({});
        setActiveGroup(null);
        dispatch({ type: 'SET_STUDENTS_DATA', payload: [] });
      }

      setAssessmentSyncNeeded(false);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      console.error('Error loading course data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForAssessmentPlanChanges = () => {
    if (!state.activeCourse) return;

    // Compare current assessments with global state assessments
    const globalAssessments = state.assessmentPlan;

    // If global assessments are different from current assessments, sync is needed
    const globalIds = globalAssessments.map(a => a.id).sort();
    const currentIds = currentAssessments.map(a => a.id).sort();
    const isDifferent = globalIds.length !== currentIds.length || globalIds.some((id, index) => id !== currentIds[index]);

    if (isDifferent && globalAssessments.length > 0) {
      console.log('Assessment plan has changed, sync needed');
      console.log('Global:', globalIds);
      console.log('Current:', currentIds);
      setAssessmentSyncNeeded(true);
    }
  };

  const syncWithLatestAssessments = async () => {
    if (!state.activeCourse) return;

    setLoading(true);
    try {
      console.log('Syncing with latest assessments...');

      // Clean up any orphaned marks first
      await assessmentService.cleanupOrphanedMarks(state.activeCourse.id);

      // Reload fresh data
      await loadCourseData();

      alert('Successfully synchronized with latest assessment plan!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error synchronizing: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const addStudent = () => {
    const newStudent = {
      id: `temp_${Date.now()}`,
      matricId: '',
      name: '',
      marks: {},
      classGroup: activeGroup || classGroup || 'Ungrouped',
      faculty: classGroupFaculty || ''
    };
    setStudents([...students, newStudent]);
  };

  const updateStudent = (id, field, value) => {
    setStudents(students.map(student => 
      student.id === id ? { ...student, [field]: value } : student
    ));
  };

  const updateStudentMark = (studentId, assessmentId, mark) => {
    setStudents(students.map(student => 
      student.id === studentId ? 
        { ...student, marks: { ...student.marks, [assessmentId]: mark === '' ? null : mark } } : 
        student
    ));
  };

  const removeStudent = (id) => {
    setStudents(students.filter(student => student.id !== id));
  };

  const clearStudentMarks = (studentId) => {
    setStudents(students.map(student => 
      student.id === studentId ? { ...student, marks: {} } : student
    ));
  };

  const clearAllData = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    setClearLoading(true);
    try {
      const { error } = await studentService.clearAllStudents(state.activeCourse.id);
      if (error) throw new Error(error);

      setStudents([]);
      setStudentGroups({});
      setActiveGroup(null);
      dispatch({ type: 'SET_STUDENTS_DATA', payload: [] });
      setShowClearConfirmModal(false);
      alert('All student data has been cleared successfully!');
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      alert('Error clearing student data: ' + error.message);
    } finally {
      setClearLoading(false);
    }
  };

  const calculateTotal = (student) => {
    return assessments.reduce((total, assessment) => {
      const mark = student.marks[assessment.id];
      const numericMark = parseFloat(mark);
      return total + ((!isNaN(numericMark) && mark !== null && mark !== undefined && mark !== '') ? numericMark : 0);
    }, 0);
  };

  const calculateGrade = (total) => {
    if (total >= 90) return 'A+';
    if (total >= 80) return 'A';
    if (total >= 75) return 'A-';
    if (total >= 70) return 'B+';
    if (total >= 65) return 'B';
    if (total >= 60) return 'B-';
    if (total >= 55) return 'C+';
    if (total >= 50) return 'C';
    if (total >= 45) return 'C-';
    if (total >= 40) return 'D';
    return 'F';
  };

  const calculateGPA = (grade) => {
    const gpaMap = {
      'A+': 4.0,
      'A': 4.0,
      'A-': 3.7,
      'B+': 3.3,
      'B': 3.0,
      'B-': 2.7,
      'C+': 2.3,
      'C': 2.0,
      'C-': 1.7,
      'D': 1.0,
      'F': 0.0
    };
    return gpaMap[grade] || 0.0;
  };

  const getPassFail = (total) => {
    return total >= 40 ? 'Pass' : 'Fail';
  };

  // Calculate assessment type totals
  const getAssessmentTypeTotals = () => {
    const totals = {
      'Continuous Assessment': 0,
      'Final Assessment': 0,
      total: 0
    };

    assessments.forEach(assessment => {
      if (assessment.type === 'Continuous Assessment' || assessment.type === 'Final Assessment') {
        totals[assessment.type] += assessment.marks;
      }
      totals.total += assessment.marks;
    });

    return totals;
  };

  const getAssessmentTypePercentage = (type) => {
    const typeTotals = getAssessmentTypeTotals();
    return typeTotals.total > 0 ? Math.round((typeTotals[type] / typeTotals.total) * 100) : 0;
  };

  // Get color for assessment type
  const getAssessmentTypeColor = (type, isBg = false) => {
    if (type === 'Continuous Assessment') {
      return isBg ? 'bg-blue-100' : 'text-blue-800 border-blue-300';
    } else if (type === 'Final Assessment') {
      return isBg ? 'bg-purple-100' : 'text-purple-800 border-purple-300';
    }
    return isBg ? 'bg-gray-100' : 'text-gray-800 border-gray-300';
  };

  // Create a new class group
  const createClassGroup = () => {
    if (!classGroup.trim()) {
      alert('Please enter a class group name');
      return;
    }
    
    if (studentGroups[classGroup]) {
      alert('A class group with this name already exists');
      return;
    }
    
    // Create new group
    const newGroup = {
      name: classGroup,
      faculty: classGroupFaculty,
      students: []
    };
    
    // Update student groups
    setStudentGroups({
      ...studentGroups,
      [classGroup]: newGroup
    });
    
    // Set as active group
    setActiveGroup(classGroup);
    setStudents([]);
    
    // Clear input fields
    setClassGroup('');
    setClassGroupFaculty('');
    
    alert(`New class group "${classGroup}" created. You can now add students to this group.`);
  };
  
  // Switch to a different class group
  const switchClassGroup = (groupName) => {
    if (studentGroups[groupName]) {
      setActiveGroup(groupName);
      setStudents(studentGroups[groupName].students);
      
      // Pre-fill class group and faculty fields for convenience
      setClassGroup(groupName);
      setClassGroupFaculty(studentGroups[groupName].faculty || '');
    }
  };

  const saveData = async () => {
    if (!state.activeCourse) {
      alert('Please select a course first');
      return;
    }

    // Validate that all students have required data
    const invalidStudents = students.filter(student => 
      !student.matricId.trim() || !student.name.trim()
    );

    if (invalidStudents.length > 0) {
      alert('Please fill in Matric ID and Name for all students before saving');
      return;
    }

    // Check if we have valid assessments
    if (currentAssessments.length === 0) {
      alert('No assessments found. Please create an assessment plan first.');
      return;
    }

    setSaveLoading(true);
    setSaveError(null);
    try {
      console.log('Saving student marks...');
      console.log('Current assessments:', currentAssessments);
      console.log('Students to save:', students.length);

      const validAssessmentIds = currentAssessments.map(a => a.id);
      if (validAssessmentIds.length === 0) {
        throw new Error('No valid assessments found. Please check your assessment plan.');
      }

      // Add class group and faculty information to students
      const studentsToSave = students.map(student => ({
        ...student,
        marks: student.marks || {},
        class_group: student.classGroup || activeGroup || 'Ungrouped',
        faculty: student.faculty || classGroupFaculty || ''
      }));

      const { data, error } = await studentService.saveStudents(
        state.activeCourse.id,
        studentsToSave,
        validAssessmentIds
      );

      if (error) throw new Error(error);

      // Record save timestamp
      setLastSaved(new Date());

      // Update the current group's students in the studentGroups state
      if (activeGroup) {
        const updatedGroups = { ...studentGroups };
        updatedGroups[activeGroup] = {
          ...updatedGroups[activeGroup],
          students: studentsToSave
        };
        setStudentGroups(updatedGroups);
      }

      // Reload fresh data to confirm save
      await loadCourseData();

      // Clear sync needed flag since we just saved with current assessments
      setAssessmentSyncNeeded(false);

      alert('Student marks saved successfully!');
    } catch (error) {
      setSaveError(error.message);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      console.error('Save error:', error);
      alert('Error saving student marks: ' + error.message);
    } finally {
      setSaveLoading(false);
    }
  };

  // Excel Upload Functions
  const triggerFileUpload = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileUpload = (e) => {
    setUploadError(null);
    setUploadLoading(true);
    const file = e.target.files[0];
    if (!file) {
      setUploadLoading(false);
      return;
    }

    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExt)) {
      setUploadError('Please upload an Excel file (.xlsx, .xls) or CSV file (.csv)');
      setUploadLoading(false);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size should be less than 5MB');
      setUploadLoading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        processExcelData(jsonData);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        setUploadError('Error processing file. Please check the file format and try again.');
      } finally {
        setUploadLoading(false);
      }
    };

    reader.onerror = () => {
      setUploadError('Error reading file. Please try again.');
      setUploadLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  const processExcelData = (data) => {
    if (!data || data.length < 2) {
      setUploadError('The file does not contain enough data. Please check the template format.');
      return;
    }

    const headers = data[0];
    if (headers.length < 2 || !String(headers[0]).toLowerCase().includes('matric') || !String(headers[1]).toLowerCase().includes('name')) {
      setUploadError('Invalid file format. The first column should be "Matric ID" and the second should be "Student Name".');
      return;
    }

    const fileAssessments = [];
    const missingAssessments = [];
    for (let i = 2; i < headers.length; i++) {
      const headerName = String(headers[i]);
      const matchingAssessment = assessments.find(a => 
        a.name.toLowerCase() === headerName.toLowerCase() || 
        a.name.toLowerCase().replace(/\s+/g, '') === headerName.toLowerCase().replace(/\s+/g, '')
      );
      
      if (matchingAssessment) {
        fileAssessments.push(matchingAssessment);
      } else {
        missingAssessments.push(headerName);
      }
    }

    if (missingAssessments.length > 0) {
      setUploadError(`Some assessment names in the file don't match your current assessment plan: ${missingAssessments.join(', ')}. Please update your assessment plan first or correct the column headers.`);
      return;
    }

    const newStudents = [...students];
    const newStudentData = [];
    const existingMatricIds = new Map(students.map(s => [s.matricId, s]));

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (row.length < 2 || !row[0] || !row[1]) continue;

      const matricId = String(row[0]).trim();
      const name = String(row[1]).trim();

      const existingStudent = existingMatricIds.get(matricId);
      if (existingStudent) {
        const studentIndex = newStudents.findIndex(s => s.matricId === matricId);
        if (studentIndex >= 0) {
          const updatedMarks = { ...newStudents[studentIndex].marks };
          
          for (let j = 0; j < fileAssessments.length; j++) {
            const assessment = fileAssessments[j];
            if (assessment) {
              const cellValue = row[j + 2];
              if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
                const markValue = parseFloat(cellValue);
                if (!isNaN(markValue)) {
                  updatedMarks[assessment.id] = markValue;
                }
              } else {
                updatedMarks[assessment.id] = null;
              }
            }
          }
          
          newStudents[studentIndex] = { ...newStudents[studentIndex], marks: updatedMarks };
        }
      } else {
        const newStudent = {
          id: `temp_${Date.now()}_${i}`,
          matricId,
          name,
          marks: {},
          classGroup: activeGroup || 'Ungrouped',
          faculty: activeGroup && studentGroups[activeGroup]?.faculty || classGroupFaculty || ''
        };

        for (let j = 0; j < fileAssessments.length; j++) {
          const assessment = fileAssessments[j];
          if (assessment) {
            const cellValue = row[j + 2];
            if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
              const markValue = parseFloat(cellValue);
              if (!isNaN(markValue)) {
                newStudent.marks[assessment.id] = markValue;
              }
            }
          }
        }
        
        newStudentData.push(newStudent);
      }
    }

    setStudents([...newStudents, ...newStudentData]);
    setShowUploadModal(false);
    alert(`Excel data processed successfully! ${newStudentData.length} new students added and ${students.length - newStudentData.length} existing students updated.`);
  };

  const downloadTemplate = () => {
    if (!state.activeCourse || assessments.length === 0 || assessments[0]?.id?.startsWith('mock_')) {
      alert('Please select a course and create an assessment plan first to download a template.');
      return;
    }

    const headers = ['Matric ID', 'Student Name', ...assessments.map(a => a.name)];
    const sampleRows = [
      ['A20EC0001', 'Student 1', ...assessments.map(() => '0')],
      ['A20EC0002', 'Student 2', ...assessments.map(() => '0')]
    ];

    const wsData = [headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const colWidths = [
      { wch: 12 },
      { wch: 20 },
      ...assessments.map(() => ({ wch: 15 }))
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Marks Template');
    
    const courseCode = state.activeCourse.course_code || state.activeCourse.courseCode;
    const groupSuffix = activeGroup && activeGroup !== 'Ungrouped' ? `_${activeGroup}` : '';
    const filename = `${courseCode}${groupSuffix}_Marks_Template.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const exportCurrentMarks = () => {
    if (!state.activeCourse || students.length === 0) {
      alert('No student data available to export.');
      return;
    }

    const headers = ['Matric ID', 'Student Name', ...assessments.map(a => a.name)];
    const dataRows = students.map(student => {
      return [
        student.matricId,
        student.name,
        ...assessments.map(assessment => {
          const mark = student.marks[assessment.id];
          return (mark !== null && mark !== undefined && mark !== '') ? mark : '';
        })
      ];
    });

    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    const colWidths = [
      { wch: 12 },
      { wch: 20 },
      ...assessments.map(() => ({ wch: 15 }))
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Student Marks');
    
    const courseCode = state.activeCourse.course_code || state.activeCourse.courseCode;
    const groupSuffix = activeGroup && activeGroup !== 'Ungrouped' ? `_${activeGroup}` : '';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const filename = `${courseCode}${groupSuffix}_Marks_${timestamp}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Calculate assessment type distributions
  const assessmentTypeTotals = getAssessmentTypeTotals();
  const continuousPercentage = getAssessmentTypePercentage('Continuous Assessment');
  const finalPercentage = getAssessmentTypePercentage('Final Assessment');

  if (!state.activeCourse) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <SafeIcon icon={FiBook} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Course Selected</h3>
          <p className="text-gray-600">Please select a course to enter student marks.</p>
        </div>
      </motion.div>
    );
  }

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center h-64"
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course data...</p>
        </div>
      </motion.div>
    );
  }

  // Check if we're using mock data
  const usingMockData = !state.activeCourse || currentAssessments.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Course Info Header */}
      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
        <h2 className="text-xl font-bold text-green-900 mb-2">
          {state.activeCourse.course_name || state.activeCourse.courseName} ({state.activeCourse.course_code || state.activeCourse.courseCode})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
          <div>
            <span className="font-medium text-green-700">Faculty:</span>
            <p className="text-green-600">{state.activeCourse.faculty}</p>
          </div>
          <div>
            <span className="font-medium text-green-700">Programme:</span>
            <p className="text-green-600">{state.activeCourse.programme}</p>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4 text-green-700" />
            <div>
              <span className="font-medium text-green-700">Semester:</span>
              <p className="text-green-600">{state.activeCourse.semester}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-green-700" />
            <div>
              <span className="font-medium text-green-700">Session:</span>
              <p className="text-green-600">{state.activeCourse.session}</p>
            </div>
          </div>
          <div>
            <span className="font-medium text-green-700">Total Students:</span>
            <p className="text-green-600">{students.length}</p>
          </div>
          <div>
            <span className="font-medium text-green-700">Total Assessments:</span>
            <p className="text-green-600">{assessments.length}</p>
          </div>
        </div>
      </div>

      {/* Class Group Selection Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900">Class Groups</h2>
          <div className="flex flex-wrap gap-2">
            {Object.keys(studentGroups).length > 0 && (
              <div className="text-sm text-blue-600 font-medium">
                {Object.keys(studentGroups).length} group(s) available
              </div>
            )}
          </div>
        </div>
        
        {/* Class Group Tabs */}
        {Object.keys(studentGroups).length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.keys(studentGroups).map(groupName => (
                <button
                  key={groupName}
                  onClick={() => switchClassGroup(groupName)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeGroup === groupName 
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                  }`}
                >
                  <div className="flex items-center">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                    <span>{groupName}</span>
                    <span className="ml-2 bg-white text-blue-800 text-xs px-2 py-0.5 rounded-full">
                      {studentGroups[groupName].students.length}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Active Group Info */}
            {activeGroup && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-blue-900">
                      <SafeIcon icon={FiUsers} className="w-4 h-4 inline mr-2" />
                      {activeGroup} Class Group
                    </h3>
                    {studentGroups[activeGroup]?.faculty && (
                      <p className="text-sm text-blue-700 mt-1">
                        <SafeIcon icon={FiHome} className="w-3 h-3 inline mr-1" />
                        Faculty: {studentGroups[activeGroup].faculty}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-blue-700">
                    {studentGroups[activeGroup]?.students.length || 0} students
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Create New Class Group */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-3">Create New Class Group</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Group Name*
              </label>
              <input
                type="text"
                value={classGroup}
                onChange={(e) => setClassGroup(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Group A, Section 1, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Faculty (Optional)
              </label>
              <input
                type="text"
                value={classGroupFaculty}
                onChange={(e) => setClassGroupFaculty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Faculty of Computer Science"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={createClassGroup}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Class Group
            </button>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <SafeIcon icon={FiInfo} className="w-4 h-4 text-yellow-600" />
            <h3 className="font-semibold text-yellow-900">Class Groups Information</h3>
          </div>
          <p className="text-yellow-800 text-sm">
            Use class groups to organize students into different sections or classes for the same course.
            Students will be saved with their class group information, allowing you to manage multiple groups separately.
          </p>
          <ul className="mt-2 text-yellow-800 text-sm list-disc pl-5 space-y-1">
            <li>Create separate class groups for different sections of the same course</li>
            <li>Easily switch between groups to manage their marks separately</li>
            <li>Export marks for specific class groups</li>
            <li>All students are automatically assigned to their class group</li>
          </ul>
        </div>
      </div>

      {/* Assessment Plan Sync Warning */}
      {assessmentSyncNeeded && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiRefreshCw} className="w-5 h-5 text-orange-600" />
              <div>
                <h4 className="font-semibold text-orange-900">Assessment Plan Updated</h4>
                <p className="text-orange-800 text-sm">
                  The assessment plan has been modified. Click "Sync Now" to update the marks table with the latest assessments.
                </p>
              </div>
            </div>
            <button
              onClick={syncWithLatestAssessments}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Warning for mock data */}
      {usingMockData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">!</span>
            </div>
            <div>
              <h4 className="font-semibold text-yellow-900">No Assessment Plan Found</h4>
              <p className="text-yellow-800 text-sm">
                No assessments found for this course. Please create and save an Assessment Plan first to enter real student marks.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save error message */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600" />
            <div>
              <h4 className="font-semibold text-red-900">Error Saving Data</h4>
              <p className="text-red-800 text-sm">{saveError}</p>
              <p className="text-red-700 text-xs mt-1">
                Try syncing with the latest assessment plan if the error persists.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Table 4: Student Marks Entry
            {activeGroup && (
              <span className="ml-2 text-lg text-blue-600">({activeGroup})</span>
            )}
          </h2>
          <div className="flex flex-wrap gap-3">
            {lastSaved && (
              <div className="text-sm text-green-600 font-medium">
                Last saved: {lastSaved.toLocaleTimeString()}
              </div>
            )}
            {!usingMockData && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowTemplateModal(true)}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  <span>Template</span>
                </button>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  <span>Upload Excel</span>
                </button>
                <button
                  onClick={exportCurrentMarks}
                  className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  <span>Export</span>
                </button>
                <button
                  onClick={syncWithLatestAssessments}
                  className="flex items-center space-x-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  <span>Sync</span>
                </button>
                <button
                  onClick={() => setShowClearConfirmModal(true)}
                  className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiTrash} className="w-4 h-4" />
                  <span>Clear All</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Assessment Distribution Summary */}
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-3">Assessment Distribution:</h3>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="px-3 py-1 rounded-lg bg-blue-100 border border-blue-200 text-blue-800 flex items-center">
              <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
              <span>Continuous Assessment: {continuousPercentage}%</span>
            </div>
            <div className="px-3 py-1 rounded-lg bg-purple-100 border border-purple-200 text-purple-800 flex items-center">
              <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
              <span>Final Assessment: {finalPercentage}%</span>
            </div>
            <div className="px-3 py-1 rounded-lg bg-green-100 border border-green-200 text-green-800">
              Total: {assessmentTypeTotals.total}%
            </div>
          </div>

          {/* Progress bar showing assessment distribution */}
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
            {continuousPercentage > 0 && (
              <div 
                className="h-full bg-blue-500 float-left" 
                style={{ width: `${continuousPercentage}%` }}
              ></div>
            )}
            {finalPercentage > 0 && (
              <div 
                className="h-full bg-purple-500 float-left" 
                style={{ width: `${finalPercentage}%` }}
              ></div>
            )}
          </div>
        </div>

        {/* Assessment Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Assessment Overview:</h3>
          {assessments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {assessments.map((assessment, index) => (
                <div 
                  key={assessment.id}
                  className={`${getAssessmentTypeColor(assessment.type, true)} p-3 rounded border ${
                    assessment.type === 'Continuous Assessment' ? 'border-blue-300' : 
                    assessment.type === 'Final Assessment' ? 'border-purple-300' : 'border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <p className={`font-medium ${
                      assessment.type === 'Continuous Assessment' ? 'text-blue-800' : 
                      assessment.type === 'Final Assessment' ? 'text-purple-800' : 'text-gray-800'
                    }`}>
                      {assessment.name}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      assessment.type === 'Continuous Assessment' ? 'bg-blue-200 text-blue-800' : 
                      assessment.type === 'Final Assessment' ? 'bg-purple-200 text-purple-800' : 'bg-gray-200 text-gray-800'
                    }`}>
                      {assessment.marks}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className={`text-xs ${
                      assessment.type === 'Continuous Assessment' ? 'text-blue-600' : 
                      assessment.type === 'Final Assessment' ? 'text-purple-600' : 'text-gray-600'
                    }`}>
                      {assessment.type}
                    </p>
                    {assessment.clo && (
                      <span className="text-xs bg-white px-1.5 py-0.5 rounded-full border border-current">
                        {assessment.clo}
                      </span>
                    )}
                  </div>
                  {usingMockData && (
                    <p className="text-xs text-yellow-600 mt-1">(Sample Data)</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-blue-700">No assessments available. Please create an Assessment Plan first.</p>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left">Matric ID</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Student Name</th>
                {assessments.map((assessment, index) => (
                  <th 
                    key={assessment.id} 
                    className={`border border-gray-300 px-4 py-2 text-center ${getAssessmentTypeColor(assessment.type, true)}`}
                  >
                    <div className="flex flex-col items-center">
                      <span>{assessment.name}</span>
                      <span className="text-xs">({assessment.marks}%)</span>
                      <span className={`text-xs px-2 py-0.5 mt-1 rounded-full ${
                        assessment.type === 'Continuous Assessment' ? 'bg-blue-200 text-blue-800' : 
                        assessment.type === 'Final Assessment' ? 'bg-purple-200 text-purple-800' : 'bg-gray-200 text-gray-800'
                      }`}>
                        {assessment.type === 'Continuous Assessment' ? 'CA' : 
                         assessment.type === 'Final Assessment' ? 'Final' : 'Other'}
                      </span>
                    </div>
                  </th>
                ))}
                <th className="border border-gray-300 px-4 py-2 text-center">Total</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Grade</th>
                <th className="border border-gray-300 px-4 py-2 text-center">GPA</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Pass/Fail</th>
                <th className="border border-gray-300 px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 ? (
                <tr>
                  <td colSpan={7 + assessments.length} className="text-center py-4 border border-gray-300">
                    <p className="text-gray-500">
                      {activeGroup ? 
                        `No students added to the "${activeGroup}" class group yet.` :
                        'No students added yet. Click "Add Student" to begin or create a class group first.'
                      }
                    </p>
                  </td>
                </tr>
              ) : (
                students.map((student) => {
                  const total = calculateTotal(student);
                  const grade = calculateGrade(total);
                  const gpa = calculateGPA(grade);
                  const passFailStatus = getPassFail(total);

                  return (
                    <tr key={student.id}>
                      <td className="border border-gray-300 px-4 py-2">
                        <input
                          type="text"
                          value={student.matricId}
                          onChange={(e) => updateStudent(student.id, 'matricId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Matric ID"
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <input
                          type="text"
                          value={student.name}
                          onChange={(e) => updateStudent(student.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Student Name"
                        />
                      </td>
                      {assessments.map(assessment => (
                        <td 
                          key={assessment.id} 
                          className={`border border-gray-300 px-4 py-2 ${getAssessmentTypeColor(assessment.type, true)}`}
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            value={student.marks[assessment.id] === null || student.marks[assessment.id] === undefined ? '' : student.marks[assessment.id]}
                            onChange={(e) => updateStudentMark(student.id, assessment.id, e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 text-center ${
                              assessment.type === 'Continuous Assessment' ? 'border-blue-300 focus:ring-blue-500' : 
                              assessment.type === 'Final Assessment' ? 'border-purple-300 focus:ring-purple-500' : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            placeholder="0"
                            disabled={usingMockData}
                            style={{ appearance: 'textfield', MozAppearance: 'textfield', WebkitAppearance: 'none' }}
                            onKeyPress={(e) => {
                              if (!/[\d.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </td>
                      ))}
                      <td className="border border-gray-300 px-4 py-2 text-center font-medium">
                        {total.toFixed(1)}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center font-medium">
                        {grade}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center font-medium">
                        {gpa.toFixed(1)}
                      </td>
                      <td className={`border border-gray-300 px-4 py-2 text-center font-medium ${passFailStatus === 'Pass' ? 'text-green-600' : 'text-red-600'}`}>
                        {passFailStatus}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => clearStudentMarks(student.id)}
                            className="text-yellow-600 hover:text-yellow-800 p-1"
                            title="Clear All Marks"
                          >
                            <SafeIcon icon={FiDelete} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeStudent(student.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove Student"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Statistics */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800">Total Students</h4>
            <p className="text-xl font-bold text-blue-600">{students.length}</p>
          </div>
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800">Pass Count</h4>
            <p className="text-xl font-bold text-green-600">
              {students.filter(s => getPassFail(calculateTotal(s)) === 'Pass').length}
            </p>
          </div>
          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800">Fail Count</h4>
            <p className="text-xl font-bold text-red-600">
              {students.filter(s => getPassFail(calculateTotal(s)) === 'Fail').length}
            </p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800">Average Score</h4>
            <p className="text-xl font-bold text-purple-600">
              {students.length > 0 ? (students.reduce((sum, s) => sum + calculateTotal(s), 0) / students.length).toFixed(1) : '0.0'}%
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-between">
          <button
            onClick={addStudent}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add Student</span>
          </button>
          <button
            onClick={saveData}
            disabled={saveLoading || usingMockData}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>{saveLoading ? 'Saving...' : 'Save Marks'}</span>
          </button>
        </div>

        {/* Success Message */}
        {!usingMockData && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">✅ Assessment Plan Synchronization:</h4>
            <p className="text-green-800 text-sm">
              The marks table is automatically synchronized with the current Assessment Plan. When you save marks, the current assessments are preserved with the data.
              {lastSaved && (
                <span className="block mt-1">
                  <strong>Last saved:</strong> {lastSaved.toLocaleString()}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Excel Upload Info */}
        {!usingMockData && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">📊 Excel Upload & Data Management:</h4>
            <div className="text-blue-800 text-sm space-y-2">
              <p>
                <strong>Upload marks:</strong> Use the "Upload Excel" feature to bulk import student marks. Download the template first to ensure proper formatting.
              </p>
              <p>
                <strong>Delete marks:</strong> To delete individual marks, clear the cell (leave empty) and save. In Excel uploads, empty cells will remove existing marks from the database.
              </p>
              <p>
                <strong>Assessment synchronization:</strong> The marks table automatically updates when the Assessment Plan changes. Use "Sync Now" to refresh the table.
              </p>
              <p>
                <strong>Class groups:</strong> Students will be assigned to the currently active class group when imported from Excel or added manually.
              </p>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-semibold text-yellow-900 mb-2">💡 Tips:</h4>
          <ul className="text-yellow-800 space-y-1 text-sm">
            <li>• Enter marks as numbers (e.g., 85, 72.5)</li>
            <li>• <strong>To delete marks: Clear the cell completely and save</strong></li>
            <li>• <strong>To clear all marks for a student:</strong> Use the <SafeIcon icon={FiDelete} className="w-3 h-3 inline" /> button</li>
            <li>• <strong>To remove a student:</strong> Use the <SafeIcon icon={FiTrash2} className="w-3 h-3 inline" /> button</li>
            <li>• <strong>To clear all data:</strong> Use the "Clear All" button (requires confirmation)</li>
            <li>• The assessment list is automatically saved when you save marks</li>
            <li>• If assessments change, use the "Sync" button to update the table</li>
            <li>• Students need 40% or above to pass</li>
            <li>• For bulk updates, use the "Upload Excel" feature with the provided template</li>
            <li>• <strong>Create class groups to manage different sections of the same course</strong></li>
            {usingMockData ? (
              <li>• <strong>Create an Assessment Plan first to save real marks</strong></li>
            ) : (
              <li>• <strong>The system automatically preserves assessment data with marks</strong></li>
            )}
          </ul>
        </div>
      </div>

      {/* Excel Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">Upload Marks from Excel</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <SafeIcon icon={FiInfo} className="w-4 h-4 mr-2" />
                  File Requirements
                </h3>
                <ul className="text-blue-800 space-y-1 text-sm">
                  <li>• Excel (.xlsx, .xls) or CSV (.csv) file format</li>
                  <li>• First column must contain Matric IDs</li>
                  <li>• Second column must contain Student Names</li>
                  <li>• Column headers must match assessment names exactly</li>
                  <li>• Marks should be numbers (e.g., 78.5)</li>
                  <li>• <strong>Empty cells will delete existing marks</strong></li>
                </ul>
                <div className="mt-3">
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    <span>Download Template</span>
                  </button>
                </div>
              </div>

              {uploadError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{uploadError}</p>
                </div>
              )}

              {/* Class Group Info */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-1">Current Class Group</h3>
                <p className="text-blue-700 text-sm">
                  Uploaded students will be added to: <strong>{activeGroup || 'Ungrouped'}</strong>
                  {activeGroup && studentGroups[activeGroup]?.faculty && (
                    <span className="block mt-1">
                      Faculty: <strong>{studentGroups[activeGroup].faculty}</strong>
                    </span>
                  )}
                </p>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={triggerFileUpload}
                  disabled={uploadLoading}
                  className="flex items-center justify-center space-x-2 px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center w-full"
                >
                  <div className="mx-auto flex flex-col items-center">
                    {uploadLoading ? (
                      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                    ) : (
                      <SafeIcon icon={FiUpload} className="w-8 h-8 text-blue-600 mb-2" />
                    )}
                    <span className="font-medium text-gray-900">
                      {uploadLoading ? 'Processing file...' : 'Click to upload Excel file'}
                    </span>
                    <span className="text-sm text-gray-500 mt-1">
                      {uploadLoading ? 'Please wait' : 'or drag and drop'}
                    </span>
                  </div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Template Info Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">Excel Template Instructions</h2>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">How to Use the Excel Template</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">1</span>
                    <div>
                      <p><strong>Download the template</strong> - Click the button below to get a pre-formatted Excel file</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">2</span>
                    <div>
                      <p><strong>Fill in student data</strong> - Enter Matric ID and Name in the first two columns</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">3</span>
                    <div>
                      <p><strong>Enter marks</strong> - Add marks for each assessment in the corresponding columns</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">4</span>
                    <div>
                      <p><strong>Save the file</strong> - Save as Excel (.xlsx) or CSV (.csv)</p>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">5</span>
                    <div>
                      <p><strong>Upload the file</strong> - Use the "Upload Excel" button in the marks entry page</p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2 flex items-center">
                  <SafeIcon icon={FiInfo} className="w-4 h-4 mr-2" />
                  Important Notes
                </h4>
                <ul className="text-yellow-800 space-y-1 text-sm">
                  <li>• <strong>Do not change column headers</strong> - They must match assessment names exactly</li>
                  <li>• <strong>Matric IDs must be unique</strong> - Duplicate IDs will update the first occurrence</li>
                  <li>• <strong>Existing students will be updated</strong> - New students will be added</li>
                  <li>• <strong>Empty cells will delete existing marks</strong> - Leave blank to remove marks</li>
                  <li>• <strong>Class group assignment</strong> - All uploaded students will be assigned to the currently selected class group</li>
                  <li>• <strong>Maximum file size:</strong> 5MB</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Example Template Format</h3>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 border-r border-b border-gray-300 text-left">Matric ID</th>
                        <th className="px-4 py-2 border-r border-b border-gray-300 text-left">Student Name</th>
                        {assessments.slice(0, 3).map((assessment, index) => (
                          <th key={index} className="px-4 py-2 border-r border-b border-gray-300 text-left">{assessment.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-4 py-2 border-r border-b border-gray-300">A20EC0001</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">Ahmad Ali</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">85</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">72.5</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">90</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-r border-b border-gray-300">A20EC0002</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">Siti Nurhaliza</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">92</td>
                        <td className="px-4 py-2 border-r border-b border-gray-300"></td>
                        <td className="px-4 py-2 border-r border-b border-gray-300">95</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Note:</strong> In the example above, the second student has an empty cell for Assessment 2, which will delete any existing mark for that assessment.
                </p>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    downloadTemplate();
                    setShowTemplateModal(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Download Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Confirmation Modal */}
      {showClearConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-4 text-red-600">
              <SafeIcon icon={FiTrash} className="w-8 h-8 mr-3" />
              <h3 className="text-xl font-bold">Clear All Data</h3>
            </div>

            <div className="py-4">
              <p className="mb-4 text-gray-700">
                Are you sure you want to clear <strong>ALL</strong> student data for this course? This action will:
              </p>
              <ul className="mb-4 ml-6 list-disc text-gray-700">
                <li className="mb-1">Delete all students</li>
                <li className="mb-1">Delete all marks</li>
                <li className="mb-1">Delete all class groups</li>
                <li className="mb-1">This action <strong>cannot</strong> be undone</li>
              </ul>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200 mb-4">
                <p className="text-red-700 text-sm font-medium">
                  This will permanently erase all student data from this course. The assessment plan will remain unchanged.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowClearConfirmModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={clearAllData}
                disabled={clearLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {clearLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SafeIcon icon={FiTrash} className="w-4 h-4" />
                )}
                <span>{clearLoading ? 'Clearing...' : 'Yes, Clear All Data'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MarksEntry;